// src/client/components/settings/FeatureModulesTab.tsx
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Separator } from '@client/components/ui/separator';
import { Database } from 'lucide-react';
import { FormField } from '@client/components/shared/FormField';
import { Setting } from '@client/types/settings';

interface FeatureModulesTabProps {
  settings: Setting[];
  getSettingValue: (key: string, defaultValue?: string) => string;
  updateSettingValue: (key: string, value: string) => void;
}

export default function FeatureModulesTab({ 
  getSettingValue, 
  updateSettingValue 
}: FeatureModulesTabProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-4 w-4" />
          Feature Modules
        </CardTitle>
        <CardDescription>LinkedIn automation and feature settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* LinkedIn Scraper Settings */}
        <FeatureSection
          title="LinkedIn Scraper"
          description="Configure profile scraping behavior"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField
              id="scraperDelay"
              label="Scraping Delay (ms)"
              type="number"
              value={getSettingValue('features.scraper.scrapingDelay', '1000')}
              onChange={(value) => updateSettingValue('features.scraper.scrapingDelay', String(value))}
            />
            <FormField
              id="maxProfiles"
              label="Max Profiles per Page"
              type="number"
              value={getSettingValue('features.scraper.maxProfilesPerPage', '50')}
              onChange={(value) => updateSettingValue('features.scraper.maxProfilesPerPage', String(value))}
            />
          </div>

          <FormField
            id="autoScrapeOnLoad"
            label="Auto-scrape on page load"
            type="switch"
            value={getSettingValue('features.scraper.autoScrapeOnLoad', 'false') === 'true'}
            onChange={(checked) => updateSettingValue('features.scraper.autoScrapeOnLoad', checked ? 'true' : 'false')}
          />

          <FormField
            id="defaultListName"
            label="Default List Name"
            value={getSettingValue('features.scraper.defaultListName', 'LinkedIn Scrape')}
            onChange={(value) => updateSettingValue('features.scraper.defaultListName', String(value))}
          />

          <FormField
            id="scraperEnabled"
            label="Enable LinkedIn Scraper"
            type="switch"
            value={getSettingValue('features.scraper.enabled', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.scraper.enabled', checked ? 'true' : 'false')}
          />
        </FeatureSection>

        <Separator />

        {/* Event Inviter Settings */}
        <FeatureSection
          title="Event Inviter"
          description="Configure event invitation automation"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField
              id="inviteBatchSize"
              label="Batch Size"
              type="number"
              value={getSettingValue('features.inviter.batchSize', '10')}
              onChange={(value) => updateSettingValue('features.inviter.batchSize', String(value))}
            />
            <FormField
              id="maxInvites"
              label="Max per Day"
              type="number"
              value={getSettingValue('features.inviter.maxInvitesPerDay', '100')}
              onChange={(value) => updateSettingValue('features.inviter.maxInvitesPerDay', String(value))}
            />
          </div>

          <FormField
            id="inviteDelay"
            label="Delay Between Invites (ms)"
            type="number"
            value={getSettingValue('features.inviter.delayBetweenInvites', '2000')}
            onChange={(value) => updateSettingValue('features.inviter.delayBetweenInvites', String(value))}
          />

          <FormField
            id="inviterEnabled"
            label="Enable Event Inviter"
            type="switch"
            value={getSettingValue('features.inviter.enabled', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.inviter.enabled', checked ? 'true' : 'false')}
          />
        </FeatureSection>

        <Separator />

        {/* Group Messenger Settings */}
        <FeatureSection
          title="Group Messenger"
          description="Configure bulk messaging automation"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField
              id="messageBatchSize"
              label="Batch Size"
              type="number"
              value={getSettingValue('features.messenger.batchSize', '5')}
              onChange={(value) => updateSettingValue('features.messenger.batchSize', String(value))}
            />
            <FormField
              id="messageDelay"
              label="Delay (ms)"
              type="number"
              value={getSettingValue('features.messenger.delayBetweenMessages', '3000')}
              onChange={(value) => updateSettingValue('features.messenger.delayBetweenMessages', String(value))}
            />
          </div>
          
          <FormField
            id="defaultTemplate"
            label="Default Message Template"
            type="textarea"
            value={getSettingValue('features.messenger.defaultTemplate', 'Hello {name}, I found your profile interesting...')}
            onChange={(value) => updateSettingValue('features.messenger.defaultTemplate', String(value))}
            placeholder="Use {name} for personalization"
          />

          <FormField
            id="messengerEnabled"
            label="Enable Group Messenger"
            type="switch"
            value={getSettingValue('features.messenger.enabled', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.messenger.enabled', checked ? 'true' : 'false')}
          />
        </FeatureSection>

        <Separator />

        {/* Single Capture Settings */}
        <FeatureSection
          title="Single Profile Capture"
          description="Configure individual profile capture behavior"
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField
              id="singleCaptureDelay"
              label="Capture Delay (ms)"
              type="number"
              value={getSettingValue('features.singleCapture.captureDelay', '500')}
              onChange={(value) => updateSettingValue('features.singleCapture.captureDelay', String(value))}
            />
            <FormField
              id="singleCaptureCategory"
              label="Default Category"
              value={getSettingValue('features.singleCapture.defaultCategory', 'unknown')}
              onChange={(value) => updateSettingValue('features.singleCapture.defaultCategory', String(value))}
            />
          </div>

          <FormField
            id="duplicateHandling"
            label="Duplicate Handling"
            type="select"
            value={getSettingValue('features.singleCapture.duplicateHandling', 'update')}
            onChange={(value) => updateSettingValue('features.singleCapture.duplicateHandling', String(value))}
            options={[
              { value: 'skip', label: 'Skip' },
              { value: 'update', label: 'Update' },
              { value: 'create', label: 'Create' },
            ]}
          />

          <FormField
            id="autoCapture"
            label="Auto-capture profiles"
            type="switch"
            value={getSettingValue('features.singleCapture.autoCapture', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.singleCapture.autoCapture', checked ? 'true' : 'false')}
          />

          <FormField
            id="singleCaptureEnabled"
            label="Enable Single Capture"
            type="switch"
            value={getSettingValue('features.singleCapture.enabled', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.singleCapture.enabled', checked ? 'true' : 'false')}
          />
        </FeatureSection>

        <Separator />

        {/* Network Scraper Settings */}
        <FeatureSection
          title="Network Scraper"
          description="Configure LinkedIn network invitation scraping"
        >
          <div className="grid grid-cols-3 gap-4">
            <FormField
              id="networkBatchSize"
              label="Batch Size"
              type="number"
              value={getSettingValue('features.networkScraper.batchSize', '50')}
              onChange={(value) => updateSettingValue('features.networkScraper.batchSize', String(value))}
            />
            <FormField
              id="networkScrollDelay"
              label="Scroll Delay (ms)"
              type="number"
              value={getSettingValue('features.networkScraper.scrollDelay', '1500')}
              onChange={(value) => updateSettingValue('features.networkScraper.scrollDelay', String(value))}
            />
            <FormField
              id="networkMaxScrolls"
              label="Max Scroll Attempts"
              type="number"
              value={getSettingValue('features.networkScraper.maxScrollAttempts', '10')}
              onChange={(value) => updateSettingValue('features.networkScraper.maxScrollAttempts', String(value))}
            />
          </div>

          <FormField
            id="networkDuplicateHandling"
            label="Duplicate Handling"
            type="select"
            value={getSettingValue('features.networkScraper.duplicateHandling', 'skip')}
            onChange={(value) => updateSettingValue('features.networkScraper.duplicateHandling', String(value))}
            options={[
              { value: 'skip', label: 'Skip' },
              { value: 'update', label: 'Update' },
              { value: 'create', label: 'Create' },
            ]}
          />

          <FormField
            id="autoDetectEnd"
            label="Auto-detect end of list for infinite scrolling"
            type="switch"
            value={getSettingValue('features.networkScraper.autoDetectEnd', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.networkScraper.autoDetectEnd', checked ? 'true' : 'false')}
          />

          <FormField
            id="networkScraperEnabled"
            label="Enable Network Scraper"
            type="switch"
            value={getSettingValue('features.networkScraper.enabled', 'true') === 'true'}
            onChange={(checked) => updateSettingValue('features.networkScraper.enabled', checked ? 'true' : 'false')}
          />
        </FeatureSection>
      </CardContent>
    </Card>
  );
}

// Helper Component for Feature Sections
function FeatureSection({ 
  title, 
  description, 
  children 
}: { 
  title: string; 
  description: string; 
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}